
import { useState } from 'react'
import { getPriceId } from '../lib/prices'

/**
 * Type-safe async JSON POST helper with error handling
 */
async function postJSON(path: string, body: any) {
  const res = await fetch(path, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body)
  })

  if (!res.ok) {
    const errorText = await res.text()
    throw new Error(errorText || `HTTP ${res.status}: ${res.statusText}`)
  }

  return res.json()
}

/**
 * Theme-aware pricing tier component with loading and disabled states
 */
interface TierProps {
  name: string
  price: string
  features: string[]
  action: () => void
  isLoading: boolean
  disabled: boolean
  priceKey: string
}

function Tier({ name, price, features, action, isLoading, disabled, priceKey }: TierProps) {
  return (
    <div
      className="card p-8 flex flex-col transition-all duration-500 ease-apple hover:scale-[1.02]"
      role="article"
      aria-labelledby={`tier-${priceKey}`}
    >
      <div className="flex flex-col gap-2">
        <h3
          id={`tier-${priceKey}`}
          className="text-2xl font-bold text-ink-800 tracking-tight"
        >
          {name}
        </h3>
        <div className="text-4xl font-extrabold text-carrot-600 tracking-tight">
          {price}
        </div>
      </div>

      <ul className="mt-6 space-y-3 text-base text-ink-700 flex-1" role="list">
        {features.map((feature, idx) => (
          <li key={idx} className="flex items-start gap-2">
            <svg
              className="w-5 h-5 text-leaf-500 mt-0.5 flex-shrink-0"
              fill="currentColor"
              viewBox="0 0 20 20"
              aria-hidden="true"
            >
              <path
                fillRule="evenodd"
                d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.857-9.809a.75.75 0 00-1.214-.882l-3.483 4.79-1.88-1.88a.75.75 0 10-1.06 1.061l2.5 2.5a.75.75 0 001.137-.089l4-5.5z"
                clipRule="evenodd"
              />
            </svg>
            <span>{feature}</span>
          </li>
        ))}
      </ul>

      <button
        className="btn-primary mt-8 relative disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none disabled:hover:scale-100"
        onClick={action}
        disabled={disabled}
        aria-label={`Get started with ${name}`}
        aria-busy={isLoading}
      >
        {isLoading ? (
          <span className="flex items-center justify-center gap-2">
            <svg
              className="animate-spin h-5 w-5 text-white"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              aria-hidden="true"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              />
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              />
            </svg>
            <span>Loading...</span>
          </span>
        ) : (
          'Get Started'
        )}
      </button>
    </div>
  )
}

/**
 * Error alert component with theme-aware styling
 */
interface ErrorAlertProps {
  message: string
  onDismiss: () => void
}

function ErrorAlert({ message, onDismiss }: ErrorAlertProps) {
  return (
    <div
      className="mt-6 p-4 rounded-2xl bg-accent-50 border border-accent-200 text-accent-800 shadow-soft animate-slide-down"
      role="alert"
      aria-live="assertive"
    >
      <div className="flex items-start gap-3">
        <svg
          className="w-5 h-5 text-accent-600 mt-0.5 flex-shrink-0"
          fill="currentColor"
          viewBox="0 0 20 20"
          aria-hidden="true"
        >
          <path
            fillRule="evenodd"
            d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.28 7.22a.75.75 0 00-1.06 1.06L8.94 10l-1.72 1.72a.75.75 0 101.06 1.06L10 11.06l1.72 1.72a.75.75 0 101.06-1.06L11.06 10l1.72-1.72a.75.75 0 00-1.06-1.06L10 8.94 8.28 7.22z"
            clipRule="evenodd"
          />
        </svg>
        <div className="flex-1">
          <p className="font-semibold">Payment Error</p>
          <p className="text-sm mt-1">{message}</p>
        </div>
        <button
          onClick={onDismiss}
          className="text-accent-600 hover:text-accent-800 transition-colors"
          aria-label="Dismiss error"
        >
          <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
            <path
              fillRule="evenodd"
              d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
              clipRule="evenodd"
            />
          </svg>
        </button>
      </div>
    </div>
  )
}

export default function Pricing() {
  const [loading, setLoading] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  /**
   * Initiates Stripe checkout with comprehensive error handling
   */
  const checkout = async (priceId: string, mode: 'payment' | 'subscription' = 'payment') => {
    try {
      setLoading(priceId)
      setError(null)

      // Validate price ID before making request
      if (!priceId || priceId.startsWith('price_xxx')) {
        throw new Error('Payment system is not configured. Please contact support.')
      }

      const { url } = await postJSON('/.netlify/functions/create-checkout-session', {
        priceId,
        mode,
        successUrl: window.location.origin + '/success',
        cancelUrl: window.location.origin + '/pricing'
      })

      if (!url) {
        throw new Error('No checkout URL received from server')
      }

      // Redirect to Stripe Checkout
      window.location.href = url
    } catch (err) {
      console.error('Checkout error:', err)
      const errorMessage = err instanceof Error
        ? err.message
        : 'Unable to start checkout. Please try again or contact support.'
      setError(errorMessage)
      setLoading(null)
    }
  }

  const tiers = [
    {
      key: 'strategyCallUSD',
      name: 'Strategy Call Deposit',
      price: '$99 one-time',
      features: [
        '60-minute deep dive session',
        'Credited to your first project',
        'Actionable growth roadmap'
      ],
      mode: 'payment' as const
    },
    {
      key: 'seoAuditUSD',
      name: 'SEO / PPC Audit',
      price: '$799 one-time',
      features: [
        'Comprehensive audit report & scorecard',
        'Prioritized opportunity backlog',
        '30-minute strategy review call'
      ],
      mode: 'payment' as const
    },
    {
      key: 'growthRetainerMonthlyUSD',
      name: 'Growth Retainer',
      price: 'from $2,500/mo',
      features: [
        'Full-funnel marketing operations',
        'Weekly growth sprints',
        'Real-time reporting & insights'
      ],
      mode: 'subscription' as const
    }
  ]

  return (
    <div className="container py-16">
      <header className="text-center mb-12 animate-fade-in">
        <h1 className="text-5xl font-bold text-ink-800 tracking-tight mb-4">
          Transparent Pricing
        </h1>
        <p className="text-xl text-ink-600 max-w-2xl mx-auto">
          Choose the service that fits your growth stage. No hidden fees, no surprises.
        </p>
      </header>

      <div className="grid md:grid-cols-3 gap-8 max-w-7xl mx-auto">
        {tiers.map((tier, idx) => {
          const priceId = getPriceId(tier.key as any)
          const isLoading = loading === priceId
          const isAnyLoading = loading !== null

          return (
            <div
              key={tier.key}
              className="animate-slide-up"
              style={{ animationDelay: `${idx * 100}ms` }}
            >
              <Tier
                name={tier.name}
                price={tier.price}
                features={tier.features}
                action={() => checkout(priceId, tier.mode)}
                isLoading={isLoading}
                disabled={isAnyLoading}
                priceKey={tier.key}
              />
            </div>
          )
        })}
      </div>

      {error && (
        <ErrorAlert
          message={error}
          onDismiss={() => setError(null)}
        />
      )}

      {loading && (
        <div
          className="mt-8 text-center text-carrot-600 font-medium animate-pulse-soft"
          role="status"
          aria-live="polite"
        >
          Redirecting to secure checkout...
        </div>
      )}

      <footer className="mt-12 text-center text-sm text-ink-500">
        <p>
          All payments processed securely by Stripe. Taxes may apply.{' '}
          <a
            href="/legal/refunds"
            className="text-carrot-600 hover:text-carrot-700 underline underline-offset-2"
          >
            View Refund & Cancellation Policy
          </a>
        </p>
      </footer>
    </div>
  )
}
